import React from "react";

import {
    setIsAllergenActive,
    setIsCustomerAccountActive,
    setIsOrderTakingTopBannerActive,
    setIsOrderTakingTagsActive,
    setIsOrderTakingCategoryTitleActive,
    setIsOrderTakingProductInformationActive,
    setIsCaloryActive,
    setIsPRMActive,
    setIsHelpActive,
    setSelectedSaleModeValue,
    setUserAccountOrderTakingMessageItems,
    setIsUserAccountOrderTakingActive,
    setDefaultActiveStateScreenItems,
    setDefaultStandbyStateScreenItems,
    setIsStateChanged,
    setConfigOptionsCustomerAccount,
    setIsOrderTakingSelectedCategoryTitleActive,
    setIsOrderTakingPromoCodeActive,
    setIsCommentArticleActive,
    setIsCommentRecoveryActive,
    setIsFreeCommentActive,
    setFiles,
    setIsInformationModesEmpty,
    setIsShopCartDetailsActive,
    setDesignCategories,
    setIsNutriscoreActive,
    setGeneralTransition,
    setIsOrderTakingCaloryActive,
    setIsSpecificTransitionChosen,
    setProject,
    setIsShownProductCompositionDescription,
    setIsConfirmationPopupForDeletingAnArticleActive,
    setIsCategoriesScrollActive,
    setCategoryDisplay,
    setProductDisplay,
    setCompositeProductDisplay,
    setProductAnimation,
    setConnectionItems,
    setActiveLanguages,
} from "@store";
import {
    ACCOUNT_CREATION_ID,
    ACCOUNT_CREATION_ITEM,
    GODIA_LOYALTY,
    HAS_NO_LOYALTY,
    INFORMATION_MODES_SUBTAB_INDEX,
    OPTION_ALLERGEN,
    OPTION_CALORIE,
    OPTION_PRM_CHILD,
    OPTION_ASK_FOR_HELP,
    OPTION_CUSTOMER_ACCOUNT,
    CLICK_AND_COLLECT,
    DELIVERY,
    CATEGORY_DISPLAY_MODE_1,
    CATEGORY_BORDER_STYLE_NONE,
    CATEGORY_OPACITY_MAX,
    CATEGORY_PRIMARY_SENTINEL,
    CATEGORY_SECONDARY_SENTINEL,
} from "@constants/index";

import { migrateProject } from "./migrations";
import { moveArrayElementToTheBegin } from "../../helpers/general";

export function useProjectMigrations(project: ProjectType): void {
    const templateId = project?.template?.id;
    const orderTaking = project?.template?.pages?.ways?.orderTaking as
        | OrderTakingPageType
        | undefined;
    const isMinMaxShown = orderTaking?.compositeProduct?.isMinMaxShown;
    const breadcrumbTrail = orderTaking?.orderTakingProduct?.breadcrumbTrail;
    const accountFideliteRegisterForm = (project?.template?.pages?.subSteps
        ?.accountFidelite as PageType | undefined)?.registerForm;
    const files = project?.files;

    React.useEffect(() => {
        if (!templateId) return;
        const next = migrateProject(project);
        if (next === project) return;
        if (next.files !== project.files) {
            setFiles(next.files);
        }
        setProject(next);
        // `project` is read inside but the effect must re-run only when the
        // sub-paths that drive migration predicates change. Listing `project`
        // would re-fire on every unrelated mutation; the identity-return of
        // `migrateProject` guards against loops either way.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        templateId,
        isMinMaxShown,
        breadcrumbTrail,
        accountFideliteRegisterForm,
        files,
    ]);
}

export function useTemplateScreenInit(
    project: ProjectType,
    templates: readonly ProjectTemplateType[]
): void {
    React.useEffect(() => {
        if (project.template.id === "") return;

        const templateSelected = templates.filter(
            // eslint-disable-next-line
            (defaultTemplate: ProjectTemplateType) =>
                defaultTemplate.id === project.template.id
        )[0];
        if (templateSelected === undefined) return;

        const elementsStandbyScreen = templateSelected.pages.elements
            .standbyScreen as StandbyScreenPageType | undefined;

        setDefaultActiveStateScreenItems(
            (elementsStandbyScreen !== undefined
                ? elementsStandbyScreen
                : (templateSelected.pages.ways
                      .standbyScreen as StandbyScreenPageType)
            ).items
        );

        setDefaultStandbyStateScreenItems(
            (templateSelected.content
                .standbyStateScreen as StandbyStateScreenItemsType)
                .items as StandbyScreenItemType[]
        );
    }, [project.template.id, templates]);
}

type DerivedFlagsArgs = {
    project: ProjectType;
    elements: readonly ItemsType[];
    activeSlideIndex: number;
    fideltyType: Record<string, number>;
    shop_id: string | number;
    transitions: TransitionsType;
    activeSubTab: number;
    activePageName: string;
};

export function useProjectDerivedFlagsSync(args: DerivedFlagsArgs): void {
    const {
        project,
        elements,
        activeSlideIndex,
        fideltyType,
        shop_id,
        transitions,
        activeSubTab,
        activePageName,
    } = args;

    React.useEffect(() => {
        if (project.template.id === "") return;
        if (
            !(elements.length > 0 && elements[activeSlideIndex] !== undefined)
        ) {
            return;
        }

        const orderTaking = project.template.pages.ways[
            "orderTaking"
        ] as OrderTakingPageType;

        const optionsItems = (project.template.content.options as OptionsType)
            .items;
        const optionsActiveByTitle = new Map<string, boolean>(
            optionsItems.map((item) => [item.title, item.active])
        );
        const getActiveStatus = (title: string): boolean =>
            optionsActiveByTitle.get(title) ?? false;
        const content = project.template.content as ProjectContentType;
        const languages = content.languages as LanguagesType;

        const activeLanguages = languages?.items.filter(
            (language) => language.active
        );

        setActiveLanguages(
            moveArrayElementToTheBegin(
                activeLanguages.filter(
                    (element: LanguagesItemType): boolean =>
                        element.isDefault === true
                )[0],
                activeLanguages
            )
        );
        setIsAllergenActive(getActiveStatus(OPTION_ALLERGEN));
        setIsCaloryActive(getActiveStatus(OPTION_CALORIE));
        setIsPRMActive(getActiveStatus(OPTION_PRM_CHILD));
        setIsHelpActive(getActiveStatus(OPTION_ASK_FOR_HELP));

        const selectedSaleMethodDeliveryExist = (project.template.content
            .salesMethods as ProjectContentItemType).items.filter(
            // eslint-disable-next-line
            (sale: ProjectMainContentItemType) => sale.name === DELIVERY
        )[0];

        if (fideltyType[`${shop_id}`] === HAS_NO_LOYALTY) {
            setIsCustomerAccountActive(false);
            setConfigOptionsCustomerAccount(false);
        } else {
            if (
                selectedSaleMethodDeliveryExist !== undefined &&
                selectedSaleMethodDeliveryExist.active === true
            ) {
                setIsCustomerAccountActive(true);
                setIsStateChanged(true);
                setConfigOptionsCustomerAccount(true);
            } else {
                setIsCustomerAccountActive(
                    getActiveStatus(OPTION_CUSTOMER_ACCOUNT)
                );
            }

            if (fideltyType[`${shop_id}`] === GODIA_LOYALTY) {
                const itemExists = (project.template.content
                    .connection as ProjectContentItemType).items.some(
                    (item: ProjectMainContentItemType) =>
                        item?.id === ACCOUNT_CREATION_ID
                );
                if (!itemExists) {
                    const currentItems = (project.template.content
                        .connection as ProjectContentItemType).items;
                    setConnectionItems([
                        ACCOUNT_CREATION_ITEM,
                        ...currentItems,
                    ] as ProjectMainContentItemType[]);
                }
            }
        }

        setIsOrderTakingTopBannerActive(orderTaking.topBanner.active);
        setIsOrderTakingTagsActive(orderTaking.isTags ?? false);
        setIsOrderTakingSelectedCategoryTitleActive(
            orderTaking.orderTakingProduct.selectedCategoryTitle
        );
        setIsOrderTakingPromoCodeActive(orderTaking.summary.promoCodeActive);
        setIsOrderTakingCategoryTitleActive(
            orderTaking.orderTakingProduct.categoryTitle
        );
        setIsOrderTakingProductInformationActive(
            orderTaking.orderTakingProduct.productAccessIcon.active
        );
        setIsShopCartDetailsActive(
            orderTaking.orderTakingProduct.isShopCartDetailsActive
        );
        setDesignCategories(orderTaking.orderTakingProduct.designCategories);
        setIsOrderTakingCaloryActive(
            orderTaking.orderTakingProduct.calorieIcon.active
        );
        setIsShownProductCompositionDescription(
            orderTaking.orderTakingProduct.isShownProductCompositionDescription
        );
        setIsConfirmationPopupForDeletingAnArticleActive(
            orderTaking.orderTakingProduct
                .isConfirmationPopupForDeletingAnArticleActive
        );
        setIsCategoriesScrollActive(
            orderTaking.orderTakingProduct.isCategoriesScrollActive
        );
        setCategoryDisplay({
            categoryDisplayMode:
                orderTaking.orderTakingProduct.categoryDisplayMode ??
                CATEGORY_DISPLAY_MODE_1,
            categoryDisplayBackgroundColor:
                orderTaking.orderTakingProduct.categoryDisplayBackgroundColor ??
                CATEGORY_PRIMARY_SENTINEL,
            categoryDisplayBackgroundOpacity:
                orderTaking.orderTakingProduct
                    .categoryDisplayBackgroundOpacity ?? CATEGORY_OPACITY_MAX,
            categoryDisplaySelectedTextColor:
                orderTaking.orderTakingProduct
                    .categoryDisplaySelectedTextColor ??
                CATEGORY_SECONDARY_SENTINEL,
            categoryDisplayBorderColor:
                orderTaking.orderTakingProduct.categoryDisplayBorderColor ??
                CATEGORY_PRIMARY_SENTINEL,
            categoryDisplaySvgBorder:
                orderTaking.orderTakingProduct.categoryDisplaySvgBorder ??
                // Rétro-compat : anciens templates persistés sous le nom
                // `categoryDisplayBorderSvg`. À retirer quand tous les
                // templates auront été migrés vers `categoryDisplaySvgBorder`.
                orderTaking.orderTakingProduct.categoryDisplayBorderSvg ??
                "",
            categoryDisplaySvgBorderName:
                orderTaking.orderTakingProduct.categoryDisplaySvgBorderName ??
                "",
            categoryDisplayBorderStyle:
                orderTaking.orderTakingProduct.categoryDisplayBorderStyle ??
                CATEGORY_BORDER_STYLE_NONE,
        });

        setProductDisplay({
            productDisplayMode:
                orderTaking.orderTakingProduct.productDisplayMode ??
                CATEGORY_DISPLAY_MODE_1,
            productDisplayBackgroundColor:
                orderTaking.orderTakingProduct.productDisplayBackgroundColor ??
                CATEGORY_PRIMARY_SENTINEL,
            productDisplayBackgroundOpacity:
                orderTaking.orderTakingProduct
                    .productDisplayBackgroundOpacity ?? CATEGORY_OPACITY_MAX,
            productDisplaySelectedTextColor:
                orderTaking.orderTakingProduct
                    .productDisplaySelectedTextColor ??
                CATEGORY_SECONDARY_SENTINEL,
            productDisplayBorderColor:
                orderTaking.orderTakingProduct.productDisplayBorderColor ??
                CATEGORY_PRIMARY_SENTINEL,
            productDisplaySvgBorder:
                orderTaking.orderTakingProduct.productDisplaySvgBorder ?? "",
            productDisplaySvgBorderName:
                orderTaking.orderTakingProduct.productDisplaySvgBorderName ??
                "",
            productDisplayBorderStyle:
                orderTaking.orderTakingProduct.productDisplayBorderStyle ??
                CATEGORY_BORDER_STYLE_NONE,
            productDisplayMode1BackgroundColor:
                orderTaking.orderTakingProduct
                    .productDisplayMode1BackgroundColor ??
                CATEGORY_PRIMARY_SENTINEL,
            productDisplayMode1BackgroundOpacity:
                orderTaking.orderTakingProduct
                    .productDisplayMode1BackgroundOpacity ??
                CATEGORY_OPACITY_MAX,
            productDisplayMode1SelectedTextColor:
                orderTaking.orderTakingProduct
                    .productDisplayMode1SelectedTextColor ??
                CATEGORY_SECONDARY_SENTINEL,
            productDisplayMode1BorderColor:
                orderTaking.orderTakingProduct.productDisplayMode1BorderColor ??
                CATEGORY_PRIMARY_SENTINEL,
            productDisplayMode1SvgBorder:
                orderTaking.orderTakingProduct.productDisplayMode1SvgBorder ??
                "",
            productDisplayMode1SvgBorderName:
                orderTaking.orderTakingProduct
                    .productDisplayMode1SvgBorderName ?? "",
            productDisplayMode1BorderStyle:
                orderTaking.orderTakingProduct.productDisplayMode1BorderStyle ??
                CATEGORY_BORDER_STYLE_NONE,
        });
        setProductAnimation(orderTaking.compositeProduct.productAnimation);

        // Affichage produit de l'ÉTAPE produit composé (ESS-3857). Persisté sous
        // `orderTaking.compositeProduct.*` — il FAUT le re-synchroniser ici à la
        // (ré)ouverture, sinon le store garde ses valeurs par défaut (= héritage
        // de l'interface générique) et les overrides sauvegardés sont perdus à
        // l'affichage. Override off + jeux par défaut quand le template ne porte
        // rien (héritage), valeurs sauvegardées sinon.
        const compositeProduct = orderTaking.compositeProduct;
        setCompositeProductDisplay({
            compositeProductDisplayOverride:
                compositeProduct.compositeProductDisplayOverride ?? false,
            compositeProductDisplayMode:
                compositeProduct.compositeProductDisplayMode ??
                CATEGORY_DISPLAY_MODE_1,
            compositeProductDisplayBackgroundColor:
                compositeProduct.compositeProductDisplayBackgroundColor ??
                CATEGORY_PRIMARY_SENTINEL,
            compositeProductDisplayBackgroundOpacity:
                compositeProduct.compositeProductDisplayBackgroundOpacity ??
                CATEGORY_OPACITY_MAX,
            compositeProductDisplaySelectedTextColor:
                compositeProduct.compositeProductDisplaySelectedTextColor ??
                CATEGORY_SECONDARY_SENTINEL,
            compositeProductDisplayBorderColor:
                compositeProduct.compositeProductDisplayBorderColor ??
                CATEGORY_PRIMARY_SENTINEL,
            compositeProductDisplayBorderStyle:
                compositeProduct.compositeProductDisplayBorderStyle ??
                CATEGORY_BORDER_STYLE_NONE,
            compositeProductDisplaySvgBorder:
                compositeProduct.compositeProductDisplaySvgBorder ?? "",
            compositeProductDisplaySvgBorderName:
                compositeProduct.compositeProductDisplaySvgBorderName ?? "",
            compositeProductDisplayMode1BackgroundColor:
                compositeProduct.compositeProductDisplayMode1BackgroundColor ??
                CATEGORY_PRIMARY_SENTINEL,
            compositeProductDisplayMode1BackgroundOpacity:
                compositeProduct.compositeProductDisplayMode1BackgroundOpacity ??
                CATEGORY_OPACITY_MAX,
            compositeProductDisplayMode1SelectedTextColor:
                compositeProduct.compositeProductDisplayMode1SelectedTextColor ??
                CATEGORY_SECONDARY_SENTINEL,
            compositeProductDisplayMode1BorderColor:
                compositeProduct.compositeProductDisplayMode1BorderColor ??
                CATEGORY_PRIMARY_SENTINEL,
            compositeProductDisplayMode1BorderStyle:
                compositeProduct.compositeProductDisplayMode1BorderStyle ??
                CATEGORY_BORDER_STYLE_NONE,
            compositeProductDisplayMode1SvgBorder:
                compositeProduct.compositeProductDisplayMode1SvgBorder ?? "",
            compositeProductDisplayMode1SvgBorderName:
                compositeProduct.compositeProductDisplayMode1SvgBorderName ??
                "",
        });

        setIsNutriscoreActive(
            orderTaking.orderTakingProduct.isNutriscoreActive
        );

        const filterInformationModeInSaleMethod = (project.template.content
            .salesMethods as ProjectContentItemType).items.filter(
            (saleMethod) =>
                saleMethod.name !== CLICK_AND_COLLECT &&
                (saleMethod.informationModes as InformationModesContentType)
                    .items.length !== 0 &&
                saleMethod.active === true
        );

        if (
            (project.template.content
                .informationModes as ProjectContentItemType).items.length ===
                0 ||
            filterInformationModeInSaleMethod.length === 0
        ) {
            setIsInformationModesEmpty(true);
        } else {
            setIsInformationModesEmpty(false);
            if (
                activeSubTab !== INFORMATION_MODES_SUBTAB_INDEX &&
                activePageName !== "informationModes"
            ) {
                setSelectedSaleModeValue(
                    filterInformationModeInSaleMethod[0].name
                );
            }
        }

        const userAccountLanguages = (orderTaking.header
            .userAccount as UserAccountType).languages;
        if (getActiveStatus(OPTION_CUSTOMER_ACCOUNT) === false) {
            setIsUserAccountOrderTakingActive(false);
        } else {
            setIsUserAccountOrderTakingActive(
                orderTaking.header.userAccount.active
            );
        }
        setUserAccountOrderTakingMessageItems(userAccountLanguages);

        setIsCommentArticleActive(
            orderTaking.orderTakingProduct.isCommentArticleActive
        );
        setIsFreeCommentActive(
            orderTaking.orderTakingProduct.isFreeCommentActive
        );
        setIsCommentRecoveryActive(false);

        const animations = Object.keys(project.template.pages.ways).map(
            (key: string) =>
                (project.template.pages.ways[key] as
                    | PageType
                    | ConsommationPageType
                    | PaymentPageType
                    | PrinterOptionsPageType).animation
        );
        if (animations.every((a) => a === animations[0])) {
            setGeneralTransition(transitions[animations[0]]);
            setIsSpecificTransitionChosen(false);
        } else {
            setGeneralTransition({
                prettyName: "",
                timeout: 0,
                className: "",
            });
            setIsSpecificTransitionChosen(true);
        }
    }, [
        activeSlideIndex,
        elements,
        project.template.content,
        project.template.id,
        project.template.pages.ways,
        transitions,
        activeSubTab,
        activePageName,
        fideltyType,
        shop_id,
    ]);
}

export function useInitialInformationModesCheck(project: ProjectType): void {
    React.useEffect(() => {
        if (project.template.id === "") return;
        setFiles(project.files);

        const informationModesItems = (project.template.content
            .informationModes as ProjectContentItemType)
            .items as ProjectMainContentItemType[];
        const salesItems = (project.template.content
            .salesMethods as ProjectContentItemType)
            .items as ProjectMainContentItemType[];

        const hasMatch = informationModesItems.filter(
            (item: ProjectMainContentItemType) =>
                salesItems.filter(
                    // eslint-disable-next-line
                    (saleMethod: ProjectMainContentItemType) =>
                        Object.keys(item.active).includes(saleMethod.name) &&
                        saleMethod.active === true
                )
        );

        const isEmpty =
            informationModesItems.length === 0 || hasMatch.length === 0;
        setIsInformationModesEmpty(isEmpty);
    }, [
        project.template.id,
        project.files,
        project.template.content.informationModes,
        project.template.content.salesMethods,
    ]);
}
